import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NAV, AUTH } from "@/constants/testIds";
import { User, Wallet as WalletIcon, Gauge, Crown, Settings, LogOut } from "lucide-react";

export default function ProfileMenu({ onPickMembership }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const initial = (user.name || user.email || "·")[0]?.toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={NAV.profileTrigger}
          aria-label="Open profile menu"
          className="w-9 h-9 rounded-full bg-[#1a1d28] border border-[#2a2f3f] flex items-center justify-center text-xs font-mono text-yellow-400 hover:bg-[#222634] transition-colors"
        >
          {initial}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        data-testid={NAV.profileMenu}
        align="end"
        className="w-60 bg-[#12141c] border-[#222634] text-slate-100"
      >
        <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
          <div className="flex items-center gap-2">
            <User className="w-3 h-3" />
            <span className="truncate">{user.name || user.email}</span>
          </div>
          <p className="mt-1 text-[10px] text-slate-500 truncate normal-case tracking-normal">
            {user.email}
          </p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#222634]" />
        <DropdownMenuItem
          data-testid={NAV.profileItemWallet}
          asChild
          className="focus:bg-[#1a1d28] cursor-pointer"
        >
          <Link to="/wallet" className="flex items-center gap-2">
            <WalletIcon className="w-4 h-4 text-yellow-400" />
            <span>Wallet</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={NAV.profileItemCai}
          asChild
          className="focus:bg-[#1a1d28] cursor-pointer"
        >
          <Link to="/dashboard" className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-emerald-400" />
            <span>CAI dashboard</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={NAV.profileItemMembership}
          onSelect={() => onPickMembership?.()}
          className="focus:bg-[#1a1d28] cursor-pointer"
        >
          <Crown className="w-4 h-4 mr-2 text-yellow-300" />
          <span>
            {user.is_member
              ? user.membership_tier === "unlimited"
                ? "Unlimited tier"
                : "Base tier · Upgrade"
              : "Join membership"}
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={NAV.profileItemSettings}
          asChild
          className="focus:bg-[#1a1d28] cursor-pointer"
        >
          <Link to="/settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-slate-400" />
            <span>Settings</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[#222634]" />
        <DropdownMenuItem
          data-testid={NAV.profileItemLogout}
          onSelect={async () => {
            await logout();
            navigate("/");
          }}
          className="focus:bg-rose-500/10 cursor-pointer text-rose-300"
        >
          <LogOut className="w-4 h-4 mr-2" />
          <span data-testid={AUTH.logoutBtn}>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
